/**
 * Multihub Lead Form Block
 *
 * A lead capture form that submits through the Multihub lead tracking system
 * (window.__mh_leads__.submitAsync). Requires the multihub-lead-capture
 * injection block to be active on the site so leadcapture.js is loaded.
 *
 * Field structure, validation, and submission logic ported from
 * widgets/src/multihub-contact-us.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, type FormEvent } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useBlockToken, useBlockSlotGroup, EditableBlockToken, EditableSlotGroup, useVibesiteEvents, type SlotGroupResult } from '@vibesites/runtime/react';
import { useProperty } from '@vibesites/runtime/react';
import { Event } from '@vibesites/runtime';
import { validateEmail, validatePhone, validateMessage, MESSAGE_MAX_LENGTH } from '../../shared/form-validation';
import { PhoneInput } from '../../shared/PhoneInput';

// ============================================================================
// Constants
// ============================================================================

const REQUIRED_FIELD_MESSAGE = 'This field is required';
const MOVE_IN_DATE_ERROR_MESSAGE = 'Please select a future date';
const RECAPTCHA_SITE_KEY = '6LdKe54sAAAAAPfxTNLSkcOsdxjLdowqHpnkN2Lp';
const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
const RECAPTCHA_VERIFY_URL = 'https://recaptcha-backend-811305215973.us-central1.run.app';

// ============================================================================
// Types
// ============================================================================

type FormState = 'idle' | 'submitting' | 'success' | 'error';

declare global {
  interface Window {
    __mh_leads__?: {
      submit: (leadData: Record<string, unknown>, additionalData: Record<string, unknown>) => void;
      submitAsync: (
        leadData: Record<string, unknown>,
        additionalData: Record<string, unknown>,
      ) => Promise<{ success: boolean }>;
    };
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

interface FieldDef {
  name: string;
  required: boolean;
  requiredMessage?: string;
  errorMessage?: string;
  validate?: (v: string) => boolean;
}

interface BedroomOption {
  label: string;
  value: string;
}

// ============================================================================
// Validation
// ============================================================================

function buildFieldDefs(
  optInEnabled: boolean,
  optInRequired: boolean,
  phoneRequired: boolean,
  minMoveInDate: string,
): Record<string, FieldDef> {
  return {
    first_name: { name: 'first_name', required: true, requiredMessage: REQUIRED_FIELD_MESSAGE },
    last_name: { name: 'last_name', required: true, requiredMessage: REQUIRED_FIELD_MESSAGE },
    email: {
      name: 'email',
      required: true,
      requiredMessage: REQUIRED_FIELD_MESSAGE,
      errorMessage: 'Enter a valid email address',
      validate: validateEmail,
    },
    phone_no: {
      name: 'phone_no',
      required: phoneRequired,
      requiredMessage: REQUIRED_FIELD_MESSAGE,
      errorMessage: 'Please enter a valid phone number',
      validate: validatePhone,
    },
    move_in_date_act: {
      name: 'move_in_date_act',
      required: true,
      requiredMessage: REQUIRED_FIELD_MESSAGE,
      errorMessage: MOVE_IN_DATE_ERROR_MESSAGE,
      validate: (v) => v >= minMoveInDate,
    },
    floorplan_chosen: { name: 'floorplan_chosen', required: true, requiredMessage: REQUIRED_FIELD_MESSAGE },
    message: {
      name: 'message',
      required: false,
      errorMessage: `Message must be ${MESSAGE_MAX_LENGTH} characters or less`,
      validate: validateMessage,
    },
    opt_in: {
      name: 'opt_in',
      required: optInEnabled && optInRequired,
      errorMessage: 'Please check the box to continue',
    },
  };
}

function validateFields(
  data: Record<string, string>,
  fields: Record<string, FieldDef>,
): Record<string, string | true> {
  const errors: Record<string, string | true> = {};
  for (const [key, field] of Object.entries(fields)) {
    const value = (data[key] ?? '').trim();
    if (field.required && !value) {
      errors[key] = field.requiredMessage ?? true;
      continue;
    }
    if (value && field.validate && !field.validate(value)) {
      errors[key] = field.errorMessage ?? true;
    }
  }
  return errors;
}

// ============================================================================
// Bedroom helpers
// ============================================================================

const BEDROOM_LABELS: Record<number, string> = {
  0: 'Studio', 1: 'One Bedroom', 2: 'Two Bedroom', 3: 'Three Bedroom',
  4: 'Four Bedroom', 5: 'Five Bedroom', 6: 'Six Bedroom', 7: 'Seven Bedroom or more',
};

const DEFAULT_BEDROOMS: BedroomOption[] = [
  { label: 'Any', value: 'any' },
  { label: 'Studio', value: '0' },
  { label: 'One Bedroom', value: '1' },
  { label: 'Two Bedroom', value: '2' },
  { label: 'Three Bedroom', value: '3' },
  { label: 'Four Bedroom or more', value: 'four_more' },
];

function bedroomOptionsFromNumbers(nums: number[]): BedroomOption[] {
  const opts: BedroomOption[] = [{ label: 'Any', value: 'any' }];
  for (const n of nums) {
    const label = BEDROOM_LABELS[n];
    if (label) opts.push({ label, value: String(n) });
  }
  return opts;
}

function parseBedroomsRange(rangeText: string | null | undefined): BedroomOption[] {
  if (!rangeText || typeof rangeText !== 'string') return DEFAULT_BEDROOMS;
  const trimmed = rangeText.trim();
  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed, 10);
    if (n >= 0 && n <= 10) return bedroomOptionsFromNumbers([n]);
  }
  const match = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
  if (match) {
    const start = parseInt(match[1], 10);
    const end = parseInt(match[2], 10);
    if (start >= 0 && end >= start && end <= 10) {
      const range: number[] = [];
      for (let i = start; i <= end; i++) range.push(i);
      return bedroomOptionsFromNumbers(range);
    }
  }
  return DEFAULT_BEDROOMS;
}

function bedroomOptionsFromFloorplans(
  floorplans: Array<{ floorplan_beds_min?: string | number | null }> | null | undefined,
): BedroomOption[] | null {
  if (!floorplans?.length) return null;
  const beds = new Set<number>();
  for (const fp of floorplans) {
    const raw = String(fp.floorplan_beds_min ?? '').toUpperCase();
    const n = raw === 'S' ? 0 : parseInt(raw, 10);
    if (!isNaN(n)) beds.add(n);
  }
  if (beds.size === 0) return null;
  return bedroomOptionsFromNumbers([...beds].sort((a, b) => a - b));
}

// ============================================================================
// Styles
// ============================================================================

const formStyles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'var(--vibe-color-background, white)',
    borderRadius: 'var(--vibe-radius-lg, 12px)',
    padding: 'var(--mh-lead-form-card-padding, 2rem)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    fontFamily: 'var(--vibe-font-body)',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontFamily: 'var(--vibe-font-heading)',
    color: '#000000',
    marginBottom: '0.5rem',
    marginTop: 0,
    textAlign: 'center' as const,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#6b7280',
    textAlign: 'center' as const,
    marginBottom: '1.5rem',
    marginTop: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--mh-lead-form-gap, 1rem)',
    minWidth: 0,
    maxWidth: '100%',
  },
  nameRow: {
    display: 'grid',
    gridTemplateColumns: 'var(--mh-lead-form-name-columns, 1fr 1fr)',
    gap: 'var(--mh-lead-form-gap, 1rem)',
    minWidth: 0,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'var(--mh-lead-form-field-columns, 1fr 1fr)',
    gap: 'var(--mh-lead-form-gap, 1rem)',
    minWidth: 0,
  },
  input: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '0.875rem 1rem',
    fontSize: '1rem',
    border: '1px solid var(--vibe-color-border, #d1d5db)',
    borderRadius: 'var(--vibe-radius-md, 8px)',
    fontFamily: 'var(--vibe-font-body)',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    color: '#111827',
    backgroundColor: 'var(--vibe-color-background, white)',
    height: '3.25rem',
  },
  inputError: {
    borderColor: 'var(--vibe-color-error, #ef4444)',
  },
  select: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '0.875rem 2.75rem 0.875rem 1rem',
    fontSize: '1rem',
    border: '1px solid var(--vibe-color-border, #d1d5db)',
    borderRadius: 'var(--vibe-radius-md, 8px)',
    fontFamily: 'var(--vibe-font-body)',
    backgroundColor: 'var(--vibe-color-background, white)',
    cursor: 'pointer',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    boxSizing: 'border-box' as const,
    color: '#111827',
    height: '3.25rem',
  },
  dropdownIcon: {
    position: 'absolute' as const,
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1rem',
    height: '1rem',
    color: '#9ca3af',
    pointerEvents: 'none' as const,
  },
  textarea: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '0.875rem 1rem',
    fontSize: '1rem',
    border: '1px solid var(--vibe-color-border, #d1d5db)',
    borderRadius: 'var(--vibe-radius-md, 8px)',
    fontFamily: 'var(--vibe-font-body)',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    color: '#111827',
    backgroundColor: 'var(--vibe-color-background, white)',
  },
  submitButton: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: 'var(--vibe-color-primary)',
    color: 'var(--vibe-color-text-on-primary, white)',
    border: 'none',
    borderRadius: 'var(--vibe-radius-md, 8px)',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    fontFamily: 'var(--vibe-font-body)',
    boxSizing: 'border-box' as const,
  },
  fieldError: {
    fontSize: '0.75rem',
    color: 'var(--vibe-color-error, #ef4444)',
    margin: '0.2rem 0 0',
  },
  fieldNotice: {
    fontSize: '0.75rem',
    color: '#6b7280',
    margin: '0.2rem 0 0',
  },
  statusError: {
    fontSize: '0.875rem',
    color: 'var(--vibe-color-error, #ef4444)',
    textAlign: 'center' as const,
    margin: 0,
  },
  optInSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.625rem',
    cursor: 'pointer',
    lineHeight: 1.4,
  },
  optInCheckbox: {
    flexShrink: 0,
    marginTop: '0.1875rem',
    width: '1rem',
    height: '1rem',
    cursor: 'pointer',
    accentColor: 'var(--vibe-color-primary, currentColor)',
  },
  optInText: {
    fontSize: '0.8125rem',
    color: '#374151',
    fontWeight: 300,
  },
  successHeading: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontFamily: 'var(--vibe-font-heading)',
    color: '#000000',
    margin: 0,
  },
  successSubtext: {
    fontSize: '0.875rem',
    color: '#6b7280',
    margin: 0,
    lineHeight: 1.5,
  },
  successCta: {
    display: 'inline-block',
    padding: '1rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: 'var(--vibe-color-primary)',
    color: 'var(--vibe-color-text-on-primary, white)',
    border: 'none',
    borderRadius: 'var(--vibe-radius-md, 8px)',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    fontFamily: 'var(--vibe-font-body)',
    boxSizing: 'border-box' as const,
    textDecoration: 'none',
    textAlign: 'center' as const,
  },
};

const FORM_CSS = `
.mh-lead-form-card form > * { min-width: 0; max-width: 100%; }
.mh-lead-form-name-row > *,
.mh-lead-form-field-row > * { min-width: 0; max-width: 100%; }
.mh-lead-form-date-field {
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  cursor: pointer;
}
input[name="move_in_date_act"] {
  display: block;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  accent-color: var(--vibe-color-primary);
}
input[name="move_in_date_act"]::-webkit-calendar-picker-indicator { display: none; -webkit-appearance: none; }
input[name="move_in_date_act"]::-webkit-inner-spin-button { display: none; }
input[name="move_in_date_act"]::-webkit-clear-button { display: none; }
input[name="move_in_date_act"]::-webkit-datetime-edit,
input[name="move_in_date_act"]::-webkit-datetime-edit-fields-wrapper,
input[name="move_in_date_act"]::-webkit-datetime-edit-text,
input[name="move_in_date_act"]::-webkit-datetime-edit-month-field,
input[name="move_in_date_act"]::-webkit-datetime-edit-day-field,
input[name="move_in_date_act"]::-webkit-datetime-edit-year-field {
  padding: 0;
}
input[name="move_in_date_act"]::-webkit-date-and-time-value {
  text-align: left;
  margin: 0;
  padding: 0;
  min-width: 0;
}
@media (max-width: 480px) {
  .mh-lead-form-card {
    --mh-lead-form-card-padding: 1.25rem;
    --mh-lead-form-gap: 0.875rem;
  }
  .mh-lead-form-field-row { --mh-lead-form-field-columns: 1fr; }
}
@keyframes mh-circle-pop { to { transform: scale(1); opacity: 1; } }
@keyframes mh-check-draw { to { stroke-dashoffset: 0; } }
.mh-lead-form-success .mh-circle {
  fill: var(--vibe-color-primary, #28c76f);
  transform-origin: 60px 60px;
  transform: scale(0.85);
  opacity: 0;
  animation: mh-circle-pop 0.65s ease-out forwards;
}
.mh-lead-form-success .mh-check {
  fill: none;
  stroke: var(--vibe-color-text-on-primary, white);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 120;
  stroke-dashoffset: 120;
  animation: mh-check-draw 0.7s ease-out 0.35s forwards;
}
@media (prefers-reduced-motion: reduce) {
  .mh-lead-form-success .mh-circle,
  .mh-lead-form-success .mh-check {
    animation: none; opacity: 1; transform: none; stroke-dashoffset: 0;
  }
}
@keyframes mh-submit-spin { to { transform: rotate(360deg); } }
.mh-lead-form-submit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.mh-lead-form-submit-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: mh-submit-spin 0.75s linear infinite;
  flex-shrink: 0;
}
@media (prefers-reduced-motion: reduce) {
  .mh-lead-form-submit-spinner { animation: none; opacity: 0.6; }
}
.mh-lead-form-success-cta,
.mh-lead-form-success-cta:visited,
.mh-lead-form-success-cta:hover,
.mh-lead-form-success-cta:focus {
  color: var(--vibe-color-text-on-primary, white);
}
.grecaptcha-badge { visibility: hidden !important; }
`;

// ============================================================================
// reCAPTCHA hook
// ============================================================================

function useRecaptcha(enabled: boolean) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  // Load the reCAPTCHA v3 script (idempotent)
  useEffect(() => {
    if (!enabled) return;
    const existing = document.querySelector(`script[src="${RECAPTCHA_SCRIPT_URL}"]`);
    if (existing) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      console.error('[MultihubLeadForm] Failed to load reCAPTCHA script');
      setScriptError(true);
    };
    document.head.appendChild(script);
  }, [enabled]);

  // Get a reCAPTCHA token and verify it against the backend
  const verifyRecaptcha = useCallback(async (): Promise<boolean> => {
    if (!enabled || scriptError) return true;
    if (!scriptLoaded || !window.grecaptcha?.enterprise) {
      console.warn('[MultihubLeadForm] reCAPTCHA not ready, allowing submission');
      return true;
    }

    try {
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha!.enterprise.ready(() => {
          window.grecaptcha!.enterprise.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then(resolve, reject);
        });
      });

      const res = await fetch(RECAPTCHA_VERIFY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (res.ok) return true;

      const body = await res.json().catch(() => null);
      console.warn('[MultihubLeadForm] reCAPTCHA verification failed:', body);
      return false;
    } catch (err) {
      console.error('[MultihubLeadForm] reCAPTCHA error:', err);
      return true; // fail open on network errors
    }
  }, [enabled, scriptLoaded, scriptError]);

  return { verifyRecaptcha };
}

// ============================================================================
// Success View
// ============================================================================

// Shared runtime definition for the success CTA slot group, used by both the
// useBlockSlotGroup hook and the inline EditableSlotGroup wrapper. The matching
// entry in `contentSchema.slotGroups.success_cta` must stay a separate inline
// literal — the schema linter requires contentSchema to be statically
// extractable, so it cannot reference this const. Keep the two in sync.
const SUCCESS_CTA_DEFINITION: SlotGroupDefinition = {
  label: 'Success CTA',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: '', description: 'Optional button shown in the thank-you state.' },
    url: { label: 'Button URL', type: 'url', defaultValue: '', description: 'URL for the thank-you button. Both label and URL are required to show the button.' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self', description: 'How to open the link: _self (same tab) or _blank (new tab).' },
  },
};

function FormSuccessView({
  sectionId,
  heading,
  subtext,
  showIcon,
  cta,
}: {
  sectionId: string;
  heading: string;
  subtext: string;
  showIcon: boolean;
  cta: SlotGroupResult;
}) {
  const ctaLabel = cta.label ?? '';
  const ctaUrl = cta.url ?? '';
  const ctaTarget = cta._target ?? '_self';
  const showCta = Boolean(ctaLabel && ctaUrl);

  return (
    <div
      className={showIcon ? 'mh-lead-form-success' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        alignItems: 'center',
        textAlign: 'center',
      }}
      role="status"
      aria-live="polite"
    >
      {showIcon && (
        <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
          <circle className="mh-circle" cx="60" cy="60" r="54" />
          <path className="mh-check" d="M36 62 L54 80 L86 44" />
        </svg>
      )}
      {heading && (
        <EditableBlockToken
          localName="success_message"
          label="Success Heading"
          defaultValue="Thank you! We will be in touch soon."
          as="h3"
          style={formStyles.successHeading}
        >
          {heading}
        </EditableBlockToken>
      )}
      {subtext && (
        <EditableBlockToken
          localName="success_subtext"
          label="Success Subtext"
          defaultValue=""
          as="p"
          style={formStyles.successSubtext}
        >
          {subtext}
        </EditableBlockToken>
      )}
      {showCta && (
        <EditableSlotGroup
          groupPath={`${sectionId}.success_cta`}
          definition={SUCCESS_CTA_DEFINITION}
        >
          <a
            className="mh-lead-form-success-cta"
            href={ctaUrl}
            target={ctaTarget === '_blank' ? '_blank' : undefined}
            rel={ctaTarget === '_blank' ? 'noopener noreferrer' : undefined}
            style={{
              ...formStyles.successCta,
              width: showIcon ? '100%' : undefined,
            }}
          >
            {ctaLabel}
          </a>
        </EditableSlotGroup>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

function MultihubLeadFormComponent({ sectionId }: BlockProps) {
  // Content tokens
  const headerText = useBlockToken('form_header_text', { defaultValue: 'Contact Us' });
  const subheaderText = useBlockToken('form_subheader_text', { defaultValue: '' });
  const firstNamePlaceholder = useBlockToken('first_name_placeholder', { defaultValue: 'First Name' });
  const lastNamePlaceholder = useBlockToken('last_name_placeholder', { defaultValue: 'Last Name' });
  const emailPlaceholder = useBlockToken('email_placeholder', { defaultValue: 'Email Address' });
  const phonePlaceholder = useBlockToken('phone_placeholder', { defaultValue: 'Phone Number' });
  const phoneRequiredToken = useBlockToken('phone_required', { defaultValue: 'true' });
  const messagePlaceholder = useBlockToken('message_placeholder', { defaultValue: 'Message (optional)' });
  const successMessage = useBlockToken('success_message', { defaultValue: 'Thank you! We will be in touch soon.' });
  const successSubtext = useBlockToken('success_subtext', { defaultValue: '' });
  const showSuccessIconToken = useBlockToken('show_success_icon', { defaultValue: 'true' });
  const errorMessage = useBlockToken('error_message', { defaultValue: 'Something went wrong. Please try again.' });
  const showMessageField = useBlockToken('show_message_field', { defaultValue: 'true' });
  const bedroomListType = useBlockToken('bedroom_list_type', { defaultValue: 'derived' });
  const optInEnabledToken = useBlockToken('opt_in_enabled', { defaultValue: 'false' });
  const optInRequiredToken = useBlockToken('opt_in_required', { defaultValue: 'false' });
  const optInDisclaimerText = useBlockToken('opt_in_disclaimer', { defaultValue: 'I agree to receive communications.' });

  const captchaEnabledToken = useBlockToken('captcha_enabled', { defaultValue: 'true' });

  const optInEnabled = optInEnabledToken === 'true';
  const optInRequired = optInRequiredToken === 'true';
  const phoneRequired = phoneRequiredToken !== 'false';
  const captchaEnabled = captchaEnabledToken === 'true';
  const showSuccessIcon = showSuccessIconToken !== 'false';

  // Submit button slot group — button label + optional redirect after submission
  const submitButton = useBlockSlotGroup('submit_button', {
    label: 'Submit Button',
    fields: {
      label: { label: 'Button Text', type: 'text', defaultValue: 'Send Message', description: 'Text displayed on the submit button.' },
      loading_label: { label: 'Loading Text', type: 'text', defaultValue: 'Submitting...', description: 'Text displayed on the submit button while the form is posting.' },
      url: { label: 'Redirect URL', type: 'url', defaultValue: '', description: 'URL to redirect to after successful submission. Leave empty for no redirect.' },
      _target: { label: 'Redirect Target', type: 'text', defaultValue: '_self', description: 'How to open the redirect: _self (same tab) or _blank (new tab).' },
    },
  });

  const successCta = useBlockSlotGroup('success_cta', SUCCESS_CTA_DEFINITION);

  // Runtime event bus — emits contact_form_submitted on success.
  const { emit } = useVibesiteEvents();

  // Property data for derived bedrooms
  const property = useProperty();
  const bedroomOptions = useMemo(() => {
    if (bedroomListType === 'derived') {
      const fromFp = bedroomOptionsFromFloorplans(property?.floorplans);
      if (fromFp) return fromFp;
      return parseBedroomsRange(property?.property_bedrooms_range);
    }
    return DEFAULT_BEDROOMS;
  }, [bedroomListType, property?.floorplans, property?.property_bedrooms_range]);

  // Form state
  const [formState, setFormState] = useState<FormState>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | true>>({});
  const [statusMessage, setStatusMessage] = useState('');
  const [moveInDateNotice, setMoveInDateNotice] = useState('');
  const [dateSelected, setDateSelected] = useState(false);
  const [bedroomSelected, setBedroomSelected] = useState(false);
  const firstErrorRef = useRef<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef(false);
  const submittingRef = useRef(false);

  const { verifyRecaptcha } = useRecaptcha(captchaEnabled);

  const tomorrowDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const fieldDefs = useMemo(
    () => buildFieldDefs(optInEnabled, optInRequired, phoneRequired, tomorrowDate),
    [optInEnabled, optInRequired, phoneRequired, tomorrowDate],
  );
  const errorId = useCallback((name: string) => `${sectionId}-${name}-error`, [sectionId]);

  // Inject CSS for browser pseudo-elements and responsive rules.
  useEffect(() => {
    if (styleRef.current) return;
    styleRef.current = true;
    const style = document.createElement('style');
    style.textContent = FORM_CSS;
    style.dataset.block = 'mh-lead-form';
    document.head.appendChild(style);
    return () => { style.remove(); styleRef.current = false; };
  }, []);

  // Focus first error field
  useEffect(() => {
    if (firstErrorRef.current && formRef.current) {
      const fieldName = firstErrorRef.current;
      const el = formRef.current.querySelector<HTMLElement>(
        `[data-field-name="${fieldName}"], [name="${fieldName}"]:not([type="hidden"])`,
      );
      el?.focus();
      firstErrorRef.current = null;
    }
  }, [fieldErrors]);

  useEffect(() => {
    if (formState !== 'success' || submitButton.url) return;
    if (!window.matchMedia('(max-width: 480px)').matches) return;

    const frame = window.requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => { window.cancelAnimationFrame(frame); };
  }, [formState, submitButton.url]);

  const clearFieldError = useCallback((name: string) => {
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const validateField = useCallback((name: string) => {
    const field = fieldDefs[name];
    if (!field || !formRef.current) return;

    const value = new FormData(formRef.current).get(name);
    const fieldErrors = validateFields(
      { [name]: typeof value === 'string' ? value.trim() : '' },
      { [name]: field },
    );

    setFieldErrors((prev) => {
      const next = { ...prev };
      if (fieldErrors[name]) next[name] = fieldErrors[name];
      else delete next[name];
      return next;
    });
  }, [fieldDefs]);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setStatusMessage('');

    const formData = new FormData(e.currentTarget);
    const data: Record<string, string> = {};
    formData.forEach((v, k) => { data[k] = typeof v === 'string' ? v.trim() : ''; });

    // Validate
    const errors = validateFields(data, fieldDefs);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      firstErrorRef.current = Object.keys(errors)[0];
      setStatusMessage('Please address the errors above');
      submittingRef.current = false;
      return;
    }

    // Loading state covers reCAPTCHA verification and lead POST.
    setFormState('submitting');
    setFieldErrors({});

    // Verify reCAPTCHA if enabled
    if (captchaEnabled) {
      const isHuman = await verifyRecaptcha();
      if (!isHuman) {
        setStatusMessage('Verification failed. Please try again.');
        setFormState('idle');
        submittingRef.current = false;
        return;
      }
    }

    // Check leads script is loaded
    if (!window.__mh_leads__?.submitAsync) {
      setStatusMessage('This form was misconfigured, please try again later');
      setFormState('error');
      submittingRef.current = false;
      return;
    }

    // Build lead payload (keys match widget convention)
    const payload: Record<string, unknown> = { ...data };
    if (optInEnabled) {
      payload.opt_in = data.opt_in === 'on';
    }

    try {
      const response = await window.__mh_leads__!.submitAsync(payload, {});
      if (response?.success === true) {
        setFormState('success');

        // Emit canonical event so tracking sinks (GA4, Facebook, GTM, ...)
        // can record the lead. Sensitive fields (email/phone) are deliberately
        // NOT included — sinks should attribute leads via their own user_id /
        // event-id mechanisms, not by mirroring PII through analytics.
        try {
          emit(Event.contact_form_submitted, {
            sectionId,
            blockId: 'multihub-lead-form',
            formId: sectionId,
          });
        } catch {
          // Bus errors must never break form submission UX.
        }

        if (submitButton.url) {
          if (submitButton._target === '_blank') {
            window.open(submitButton.url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = submitButton.url;
          }
          return;
        }
      } else {
        throw new Error('Lead submission failed');
      }
    } catch {
      console.error('[MultihubLeadForm] Submission error');
      setFormState('error');
      submittingRef.current = false;
      setStatusMessage(
        errorMessage || 'There was an error sending your message. If you have an adblocker turned on, please disable it and try again.',
      );
    }
  }, [fieldDefs, optInEnabled, errorMessage, submitButton.url, submitButton._target,  captchaEnabled, verifyRecaptcha, emit, sectionId]);

  const isSubmitting = formState === 'submitting';
  const submitLabel = submitButton.label || 'Send Message';
  const loadingLabel = submitButton.loading_label || 'Submitting...';

  return (
    <div ref={cardRef} className="mh-lead-form-card" style={formStyles.card}>
      {formState === 'success' ? (
        <FormSuccessView
          sectionId={sectionId}
          heading={successMessage ?? ''}
          subtext={successSubtext ?? ''}
          showIcon={showSuccessIcon}
          cta={successCta}
        />
      ) : (
        <>
      {/* Header */}
      {headerText && (
        <EditableBlockToken
          localName="form_header_text"
          label="Form Title"
          defaultValue="Contact Us"
          as="h3"
          style={formStyles.title}
        >
          {headerText}
        </EditableBlockToken>
      )}
      {subheaderText && (
        <EditableBlockToken
          localName="form_subheader_text"
          label="Form Subtitle"
          defaultValue=""
          as="p"
          style={formStyles.subtitle}
        >
          {subheaderText}
        </EditableBlockToken>
      )}
      {!subheaderText && headerText && <div style={{ marginBottom: '1.5rem' }} />}

      {/* Form */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        style={formStyles.form}
        aria-label="Contact Us Form"
        noValidate
      >
        {/* Name fields */}
        <div className="mh-lead-form-name-row" style={formStyles.nameRow}>
          <div>
            <input
              type="text"
              name="first_name"
              placeholder={firstNamePlaceholder || 'First Name'}
              required
              aria-required="true"
              autoComplete="given-name"
              disabled={isSubmitting}
              onChange={() => clearFieldError('first_name')}
              onBlur={() => validateField('first_name')}
              aria-invalid={fieldErrors.first_name ? 'true' : undefined}
              aria-describedby={fieldErrors.first_name ? errorId('first_name') : undefined}
              style={{ ...formStyles.input, ...(fieldErrors.first_name ? formStyles.inputError : {}) }}
            />
            {fieldErrors.first_name && typeof fieldErrors.first_name === 'string' && (
              <p id={errorId('first_name')} style={formStyles.fieldError}>{fieldErrors.first_name}</p>
            )}
          </div>
          <div>
            <input
              type="text"
              name="last_name"
              placeholder={lastNamePlaceholder || 'Last Name'}
              required
              aria-required="true"
              autoComplete="family-name"
              disabled={isSubmitting}
              onChange={() => clearFieldError('last_name')}
              onBlur={() => validateField('last_name')}
              aria-invalid={fieldErrors.last_name ? 'true' : undefined}
              aria-describedby={fieldErrors.last_name ? errorId('last_name') : undefined}
              style={{ ...formStyles.input, ...(fieldErrors.last_name ? formStyles.inputError : {}) }}
            />
            {fieldErrors.last_name && typeof fieldErrors.last_name === 'string' && (
              <p id={errorId('last_name')} style={formStyles.fieldError}>{fieldErrors.last_name}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <input
            type="email"
            name="email"
            placeholder={emailPlaceholder || 'Email Address'}
            required
            aria-required="true"
            autoComplete="email"
            disabled={isSubmitting}
            onChange={() => clearFieldError('email')}
            onBlur={() => validateField('email')}
            aria-invalid={fieldErrors.email ? 'true' : undefined}
            aria-describedby={fieldErrors.email ? errorId('email') : undefined}
            style={{ ...formStyles.input, ...(fieldErrors.email ? formStyles.inputError : {}) }}
          />
          {fieldErrors.email && typeof fieldErrors.email === 'string' && (
            <p id={errorId('email')} style={formStyles.fieldError}>{fieldErrors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <PhoneInput
            name="phone_no"
            placeholder={phonePlaceholder || 'Phone Number'}
            required={phoneRequired}
            aria-required={phoneRequired ? 'true' : undefined}
            disabled={isSubmitting}
            onValueChange={() => clearFieldError('phone_no')}
            onFieldBlur={() => validateField('phone_no')}
            aria-invalid={fieldErrors.phone_no ? 'true' : undefined}
            aria-describedby={fieldErrors.phone_no ? errorId('phone_no') : undefined}
            style={{ ...formStyles.input, ...(fieldErrors.phone_no ? formStyles.inputError : {}) }}
          />
          {fieldErrors.phone_no && typeof fieldErrors.phone_no === 'string' && (
            <p id={errorId('phone_no')} style={formStyles.fieldError}>{fieldErrors.phone_no}</p>
          )}
        </div>

        {/* Move-in Date & Bedrooms */}
        <div className="mh-lead-form-field-row" style={formStyles.fieldRow}>
          <div>
            <div
              className="mh-lead-form-date-field"
              onClick={() => {
                const input = formRef.current?.querySelector<HTMLInputElement>('input[name="move_in_date_act"]');
                try { input?.showPicker(); } catch { input?.focus(); }
              }}
            >
              <input
                type="date"
                name="move_in_date_act"
                min={tomorrowDate}
                required
                aria-required="true"
                disabled={isSubmitting}
                onChange={(e) => {
                  // iOS WebKit ignores min/max in the picker UI (WebKit bug 225639),
                  // so clamp any past date back to the earliest allowed date.
                  if (e.currentTarget.value && e.currentTarget.value < tomorrowDate) {
                    e.currentTarget.value = tomorrowDate;
                    setMoveInDateNotice("Past dates aren't available.");
                  } else {
                    setMoveInDateNotice('');
                  }
                  setDateSelected(!!e.currentTarget.value);
                  clearFieldError('move_in_date_act');
                }}
                onBlur={() => validateField('move_in_date_act')}
                style={{
                  ...formStyles.input,
                  ...(fieldErrors.move_in_date_act ? formStyles.inputError : {}),
                  paddingRight: '2.75rem',
                  color: dateSelected ? '#111827' : 'transparent',
                  WebkitTextFillColor: dateSelected ? '#111827' : 'transparent',
                  textAlign: 'left',
                }}
                aria-label="Desired Move-in Date"
                aria-invalid={fieldErrors.move_in_date_act ? 'true' : undefined}
                aria-describedby={[
                  fieldErrors.move_in_date_act ? errorId('move_in_date_act') : '',
                  moveInDateNotice ? `${errorId('move_in_date_act')}-notice` : '',
                ].filter(Boolean).join(' ') || undefined}
              />
              {!dateSelected && (
                <span
                  style={{
                    position: 'absolute',
                    left: '1rem',
                    right: '1rem',
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    color: '#9ca3af',
                    fontSize: '1rem',
                    fontFamily: 'var(--vibe-font-body)',
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    paddingRight: '1.75rem',
                  }}
                  aria-hidden="true"
                >
                  Desired Move-in Date
                </span>
              )}
              <svg viewBox="0 0 20 20" fill="none" style={formStyles.dropdownIcon} aria-hidden="true">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {fieldErrors.move_in_date_act && typeof fieldErrors.move_in_date_act === 'string' && (
              <p id={errorId('move_in_date_act')} style={formStyles.fieldError}>{fieldErrors.move_in_date_act}</p>
            )}
            {moveInDateNotice && (
              <p id={`${errorId('move_in_date_act')}-notice`} style={formStyles.fieldNotice}>{moveInDateNotice}</p>
            )}
          </div>
          <div>
            <div style={{ position: 'relative', minWidth: 0, maxWidth: '100%' }}>
              <select
                name="floorplan_chosen"
                required
                aria-required="true"
                disabled={isSubmitting}
                onChange={(e) => {
                  clearFieldError('floorplan_chosen');
                  setBedroomSelected(!!e.currentTarget.value);
                }}
                onBlur={() => validateField('floorplan_chosen')}
                defaultValue=""
                style={{
                  ...formStyles.select,
                  ...(fieldErrors.floorplan_chosen ? formStyles.inputError : {}),
                  ...(!bedroomSelected ? { color: '#9ca3af' } : {}),
                }}
                aria-label="Desired Bedrooms"
                aria-invalid={fieldErrors.floorplan_chosen ? 'true' : undefined}
                aria-describedby={fieldErrors.floorplan_chosen ? errorId('floorplan_chosen') : undefined}
              >
                <option value="" disabled>Bedrooms</option>
                {bedroomOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <svg viewBox="0 0 20 20" fill="none" style={formStyles.dropdownIcon} aria-hidden="true">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {fieldErrors.floorplan_chosen && typeof fieldErrors.floorplan_chosen === 'string' && (
              <p id={errorId('floorplan_chosen')} style={formStyles.fieldError}>{fieldErrors.floorplan_chosen}</p>
            )}
          </div>
        </div>

        {/* Message */}
        {showMessageField !== 'false' && (
          <>
            <textarea
              name="message"
              placeholder={messagePlaceholder || 'Message (optional)'}
              rows={3}
              maxLength={MESSAGE_MAX_LENGTH}
              disabled={isSubmitting}
              onChange={() => clearFieldError('message')}
              onBlur={() => validateField('message')}
              aria-invalid={fieldErrors.message ? 'true' : undefined}
              aria-describedby={fieldErrors.message ? errorId('message') : undefined}
              style={{ ...formStyles.textarea, ...(fieldErrors.message ? formStyles.inputError : {}) }}
            />
            {fieldErrors.message && typeof fieldErrors.message === 'string' && (
              <p id={errorId('message')} style={formStyles.fieldError}>{fieldErrors.message}</p>
            )}
          </>
        )}

        {/* Opt-in */}
        {optInEnabled && (
          <div>
            <label style={formStyles.optInSection}>
              <input
                type="checkbox"
                name="opt_in"
                disabled={isSubmitting}
                onChange={() => clearFieldError('opt_in')}
                onBlur={() => validateField('opt_in')}
                style={{
                  ...formStyles.optInCheckbox,
                  ...(fieldErrors.opt_in ? { outline: '2px solid var(--vibe-color-error, #ef4444)', outlineOffset: '2px' } : {}),
                }}
                aria-required={optInRequired ? 'true' : undefined}
                aria-invalid={fieldErrors.opt_in ? 'true' : undefined}
                aria-describedby={fieldErrors.opt_in ? errorId('opt_in') : undefined}
              />
              <span style={formStyles.optInText}>{optInDisclaimerText}</span>
            </label>
            {fieldErrors.opt_in && typeof fieldErrors.opt_in === 'string' && (
              <p id={errorId('opt_in')} style={formStyles.fieldError}>{fieldErrors.opt_in}</p>
            )}
          </div>
        )}

        {/* Status message */}
        {statusMessage && (
          <p style={formStyles.statusError} role="alert" aria-live="polite">{statusMessage}</p>
        )}

        {/* Submit button */}
        <EditableSlotGroup
          groupPath={`${sectionId}.submit_button`}
          definition={{
            label: 'Submit Button',
            fields: {
              label: { label: 'Button Text', type: 'text', defaultValue: 'Send Message' },
              loading_label: { label: 'Loading Text', type: 'text', defaultValue: 'Submitting...' },
              url: { label: 'Redirect URL', type: 'url', defaultValue: '' },
              _target: { label: 'Redirect Target', type: 'text', defaultValue: '_self' },
            },
          }}
        >
          <button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting ? 'true' : undefined}
            className="mh-lead-form-submit-button"
            style={{
              ...formStyles.submitButton,
              ...(isSubmitting ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
            }}
          >
            {isSubmitting && <span className="mh-lead-form-submit-spinner" aria-hidden="true" />}
            {isSubmitting ? loadingLabel : submitLabel}
          </button>
        </EditableSlotGroup>

        {captchaEnabled && (
          <p style={{ fontSize: '0.6875rem', color: '#9ca3af', textAlign: 'center', margin: '0.5rem 0 0' }}>
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af' }}>Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af' }}>Terms of Service</a> apply.
          </p>
        )}

      </form>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'MultihubLeadForm',
  displayName: 'Lead Form',
  tokens: {
    form_header_text: {
      label: 'Header Text',
      type: 'text',
      defaultValue: 'Contact Us',
      description: 'Main heading above the form.',
    },
    form_subheader_text: {
      label: 'Subheader Text',
      type: 'text',
      defaultValue: '',
      description: 'Optional subheading below the header.',
    },
    first_name_placeholder: {
      label: 'First Name Placeholder',
      type: 'text',
      defaultValue: 'First Name',
    },
    last_name_placeholder: {
      label: 'Last Name Placeholder',
      type: 'text',
      defaultValue: 'Last Name',
    },
    email_placeholder: {
      label: 'Email Placeholder',
      type: 'text',
      defaultValue: 'Email Address',
    },
    phone_placeholder: {
      label: 'Phone Placeholder',
      type: 'text',
      defaultValue: 'Phone Number',
    },
    phone_required: {
      label: 'Require Phone Number',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to make the phone number field optional.',
    },
    message_placeholder: {
      label: 'Message Placeholder',
      type: 'text',
      defaultValue: 'Message (optional)',
    },
    success_message: {
      label: 'Success Heading',
      type: 'text',
      defaultValue: 'Thank you! We will be in touch soon.',
      description: 'Main heading shown after a successful submission.',
    },
    success_subtext: {
      label: 'Success Subtext',
      type: 'text',
      defaultValue: '',
      description: 'Optional secondary text shown below the success heading.',
    },
    show_success_icon: {
      label: 'Show Success Icon',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the animated checkmark in the thank-you state.',
    },
    error_message: {
      label: 'Error Message',
      type: 'text',
      defaultValue: 'Something went wrong. Please try again.',
    },
    show_message_field: {
      label: 'Show Message Field',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the optional message textarea.',
    },
    bedroom_list_type: {
      label: 'Bedroom List Type',
      type: 'text',
      defaultValue: 'derived',
      description: 'How to populate bedroom options: "derived" (from property floorplans/bedrooms_range) or "default" (static list).',
    },
    opt_in_enabled: {
      label: 'Enable Opt-In',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to show an opt-in checkbox.',
    },
    opt_in_required: {
      label: 'Require Opt-In',
      type: 'text',
      defaultValue: 'false',
      description: 'Set to "true" to make the opt-in checkbox required.',
    },
    opt_in_disclaimer: {
      label: 'Opt-In Disclaimer',
      type: 'text',
      defaultValue: 'I agree to receive communications.',
      description: 'Text displayed next to the opt-in checkbox.',
    },
    captcha_enabled: {
      label: 'Enable CAPTCHA',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "true" to enable invisible Google reCAPTCHA on form submission.',
    },
  },
  slotGroups: {
    submit_button: {
      label: 'Submit Button',
      description: 'Submit button label and optional redirect URL after successful form submission.',
      fields: {
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: 'Send Message',
          description: 'Text displayed on the submit button.',
        },
        loading_label: {
          label: 'Loading Text',
          type: 'text',
          defaultValue: 'Submitting...',
          description: 'Text displayed on the submit button while the form is posting.',
        },
        url: {
          label: 'Redirect URL',
          type: 'url',
          defaultValue: '',
          description: 'URL to redirect to after successful submission. Leave empty for no redirect.',
        },
        _target: {
          label: 'Redirect Target',
          type: 'text',
          defaultValue: '_self',
          description: 'How to open the redirect: _self (same tab) or _blank (new tab).',
        },
      },
    },
    success_cta: {
      label: 'Success CTA',
      description: 'Optional button shown in the thank-you state (e.g. "Back to Overview"). Requires both label and URL.',
      fields: {
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: '',
          description: 'Text displayed on the thank-you button.',
        },
        url: {
          label: 'Button URL',
          type: 'url',
          defaultValue: '',
          description: 'URL the thank-you button links to.',
        },
        _target: {
          label: 'Link Target',
          type: 'text',
          defaultValue: '_self',
          description: 'How to open the link: _self (same tab) or _blank (new tab).',
        },
      },
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const multihubLeadFormBlock: BlockDefinition = {
  id: 'multihub-lead-form',
  name: 'Lead Form',
  description: 'A lead capture form that submits through the Multihub lead tracking system.',
  category: 'Forms',
  component: MultihubLeadFormComponent,
  contentSchema,
  requirements: {
    designTokens: [
      'color.primary',
      'color.surface',
      'color.text',
      'color.textMuted',
      'color.textOnPrimary',
      'color.background',
      'color.border',
      'color.error',
      'font.heading',
      'font.body',
      'radius.md',
      'radius.lg',
    ],
    dataTokens: [],
  },
  tags: ['form', 'lead', 'contact', 'schedule', 'tour', 'inquiry', 'guest card', 'multihub'],
  defaultGridSpan: 12,
};
